import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utilities/axios";
import { getCategories, getSynthesis } from "./categories";
import { expandCategories } from "./table";
import { formattedElements } from "../../utilities/groupElementsByDate";
import { getElementsOfObjective } from "./objectives";
import { getPlancompta, getSynthesis as getSynthesisPlancompta } from "./plancompta";

const initialState = {
  elementsWithSum: [],
  elements: [],
  status: "idle",
  error: null,
  createError: null,
  elementToEdit: null,
  hoveredCell: null,
  popoverCategory: null,
  simulationInput: null,
  reellePopup: null,
  hoveredFilledCell: null,
  isEditOrAdd: null,
  hoveredPrice: null,
  elementToDelete: null,
  createStatus: "idle",
  sortedValue: "reset",
  hasUserSorted: false,
  hovredTotal: false,
  actionType: "",
  isCreatingInSimulationMode: false,
  duplicatedElementToDelete: null,
  duplicatedElementToEdit: null,
  serieToDelete: null,
  serieToEdit: null,
};

export const getElements = createAsyncThunk(
  "elements/getElements",
  async (queries, thunkAPI) => {
    let data;

    const nofilterChecked =
      !queries.params?.noFilterChecked &&
      queries.params?.filterOptions?.length === 0;

    if (queries.params) {
      delete queries.params?.noFilterChecked;
    }

    if (nofilterChecked) {
      return [];
    }

    try {
      if (!["asc", "desc"].includes(queries?.params?.sort)) {
        delete queries?.params?.sort;
      }
      const response = await axios.get(
        `/api/projects/${queries.projectId}/listing/${queries.mode}`,
        { params: queries.params }
      );
      data = await response.data;
      if (response.status === 200) {
        const formatElementByDate = formattedElements(data?.data);
        return formatElementByDate;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const getElementsWithSum = createAsyncThunk(
  "elements/getElementsWithSum",
  async (queries, thunkAPI) => {
    let data;
    delete queries?.params?.sort;

    if (
      Array.isArray(queries?.params?.filterOptions) &&
      queries?.params?.filterOptions?.length === 0
    ) {
      return [];
    }
    try {
      const response = await axios.get(
        `/api/projects/${queries.projectId}/${
          queries.isPlancompta ? "plan_comptable" : "categories"
        }/${queries.mode}/elements-sum`,
        { params: queries.params }
      );
      data = await response.data;
      if (response.status === 200) {
        return data?.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Une erreur est survenue";
      return thunkAPI.rejectWithValue(err.response?.data || { message: errorMessage });
    }
  }
);

export const createElements = createAsyncThunk(
  "elements/createElements",
  async (values, thunkAPI) => {
    let data;

    try {
      const response = await axios.post(
        `/api/categories/${values.categoryId}/element`,
        {
          designation: values?.values?.designation,
          amount: values?.values?.amount,
          dateCompta: values?.values?.dateCompta,
          dateTreso: values?.values?.dateTreso,
          type: values?.values?.type,
          status: values?.values?.status,
          ...(values?.mode === "compta" && {
            planComptableId: Number(values?.values?.planComptableId),
          }),
          ...(values?.values?.accountTresoId && {
            accountTresoId: Number(values?.values?.accountTresoId),
          }),
        }
      );
      data = await response.data;
      if (response.status === 200) {
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
        }

        if (values.isPlancompta) {
          thunkAPI.dispatch(getPlancompta(values?.project)).then(() => {
            thunkAPI.dispatch(getSynthesis(values?.synthesis)).then(() => {
              if (values?.objectiveId) {
                thunkAPI.dispatch(
                  getElementsOfObjective({
                    objectiveId: values?.objective?.objectiveId,
                    isPlancompta: true,
                  })
                );
              }
            });
          });
        }

        if (!values.isListing && !values.isPlancompta)
          thunkAPI.dispatch(getCategories(values?.project)).then(() => {
            thunkAPI.dispatch(getSynthesis(values?.synthesis));
            if (
              values?.mode === "compta" &&
              values?.objectiveId &&
              !values?.isListing
            ) {
              thunkAPI.dispatch(
                getElementsOfObjective({
                  objectiveId: values?.objective?.objectiveId,
                  isPlancompta: false,
                })
              );
            }
          });

        thunkAPI.dispatch(getElementsWithSum(values?.sum));
        if (values.isPlancompta) {
          if (!values.expandedCategories.includes(values.planComptableId))
            thunkAPI.dispatch(
              expandCategories({ key: values.planComptableId })
            );

          if (
            !values.expandedCategories.includes(
              `${values.planComptableId}-racine`
            )
          ) {
            thunkAPI.dispatch(
              expandCategories({ key: `${values.planComptableId}-racine` })
            );
          }
        } else {
          if (
            !values.expandedCategories.includes(`${values.categoryId}-racine`)
          ) {
            thunkAPI.dispatch(
              expandCategories({ key: `${values.categoryId}-racine` })
            );
          }
        }

        return data?.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Une erreur est survenue";
      return thunkAPI.rejectWithValue(err.response?.data || { message: errorMessage });
    }
  }
);

export const ventilateElement = createAsyncThunk(
  "elements/ventilateElement",
  async (values, thunkAPI) => {
    try {
      const response = await axios.post(
        `/api/elements/${values.elementId}/ventilate`,
        {
          designation: values?.values?.designation,
          amount: values?.values?.amount,
          dateCompta: values?.values?.dateCompta,
          dateTreso: values?.values?.dateTreso,
          type: values?.values?.type,
          status: values?.values?.status,
          ...(values?.values?.categoryId != null && {
            categoryId: Number(values.values.categoryId),
          }),
          ...(values?.values?.planComptableId != null &&
            values?.values?.planComptableId !== "" && {
              planComptableId: Number(values.values.planComptableId),
            }),
          ...(values?.values?.accountTresoId && {
            accountTresoId: Number(values.values.accountTresoId),
          }),
          createLiaison: !!values?.createLiaison,
        }
      );

      if (response.status === 200) {
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
        }

        if (values.isPlancompta) {
          thunkAPI.dispatch(getPlancompta(values?.project)).then(() => {
            thunkAPI.dispatch(getSynthesis(values?.synthesis)).then(() => {
              if (values?.objectiveId) {
                thunkAPI.dispatch(
                  getElementsOfObjective({
                    objectiveId: values?.objective?.objectiveId,
                    isPlancompta: true,
                  })
                );
              }
            });
          });
        }

        if (!values.isListing && !values.isPlancompta) {
          thunkAPI.dispatch(getCategories(values?.project)).then(() => {
            thunkAPI.dispatch(getSynthesis(values?.synthesis));
            if (
              values?.mode === "compta" &&
              values?.objectiveId &&
              !values?.isListing
            ) {
              thunkAPI.dispatch(
                getElementsOfObjective({
                  objectiveId: values?.objective?.objectiveId,
                  isPlancompta: false,
                })
              );
            }
          });
        }

        thunkAPI.dispatch(getElementsWithSum(values?.sum));
        return response.data?.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Une erreur est survenue";
      return thunkAPI.rejectWithValue(
        err.response?.data || { message: errorMessage }
      );
    }
  }
);

export const editElement = createAsyncThunk(
  "elements/editElement",
  async (values, thunkAPI) => {
    let data;
    try {
      const response = await axios.put(`/api/elements/${values.elementId}`, {
        designation: values?.values?.designation,
        categoryId: values?.values?.categoryId,
        amount: values?.values.amount?.toString(),
        dateCompta: values?.values?.dateCompta,
        dateTreso: values?.values?.dateTreso,
        type: values?.values?.type,
        status: values?.values?.status,
        ...(values?.values?.planComptableId && {
          planComptableId: Number(values?.values?.planComptableId),
        }),
        ...(values?.values?.accountTresoId && {
          accountTresoId: Number(values?.values?.accountTresoId),
        }),
      });
      data = await response.data;
      if (response.status === 200) {
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        } else {
          if (values?.isPlancompta) {
            thunkAPI.dispatch(getPlancompta(values?.project)).then(() => {
              thunkAPI.dispatch(getSynthesisPlancompta(values?.synthesis));
              if (values?.objectiveId) {
                thunkAPI.dispatch(
                  getElementsOfObjective({
                    objectiveId: values?.objective?.objectiveId,
                    isPlancompta: true,
                  })
                );
              }
            });
          } else {
            thunkAPI.dispatch(getCategories(values?.project)).then(() => {
              thunkAPI.dispatch(getSynthesis(values?.synthesis));
              if (values?.mode === "compta" && values?.objectiveId) {
                thunkAPI.dispatch(
                  getElementsOfObjective({
                    objectiveId: values?.objective?.objectiveId,
                    isPlancompta: false,
                  })
                );
              }
            });
          }

          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        }

        return data?.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Une erreur est survenue";
      return thunkAPI.rejectWithValue(err.response?.data || { message: errorMessage });
    }
  }
);

export const deleteElement = createAsyncThunk(
  "elements/deleteElement",
  async (values, thunkAPI) => {
    let data;

    try {
      const response = await axios.delete(`/api/elements/${values.elementId}`);
      data = await response.data;
      if (response.status === 200) {
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
        }
        thunkAPI.dispatch(setElementToDelete(null));
        if (values?.objectiveId) {
          thunkAPI.dispatch(getElementsOfObjective({
            objectiveId: values?.objectiveId,
            isPlancompta: values?.isPlancompta || false,
          }));
        }
        thunkAPI.dispatch(getElementsWithSum(values?.sum));
        if (values?.isPlancompta) {
          thunkAPI.dispatch(getPlancompta(values?.project)).then(() => {
            if (values?.objectiveId) {
              thunkAPI.dispatch(
                getElementsOfObjective({
                  objectiveId: values?.objective?.objectiveId,
                  isPlancompta: true,
                })
              );
            }
          });
        } else {
          thunkAPI.dispatch(getCategories(values?.project)).then(() => {
            thunkAPI.dispatch(getSynthesis(values?.synthesis));
            if (values?.mode === "compta" && values?.objectiveId) {
              thunkAPI.dispatch(
                getElementsOfObjective({
                  objectiveId: values?.objective?.objectiveId,
                  isPlancompta: false,
                })
              );
            }
          });
        }
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      console.error({ err });
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const duplicateOneElement = createAsyncThunk(
  "elements/duplicateOneElement",
  async (values, thunkAPI) => {
    let data;
    try {
      const response = await axios.post(
        `/api/elements/${values.elementId}/duplicate`,
        {
          designation: values?.values?.designation,
          amount: values?.values?.amount?.toString(),
          dateCompta: values?.values?.dateCompta,
          dateTreso: values?.values?.dateTreso,
          type: values?.values?.type,
          status: values?.values?.status,
          duplicationType: values?.values?.duplicationType,
          repeated: values?.values?.repeated,
        }
      );
      data = await response.data;
      if (response.status === 200) {
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
        }
        thunkAPI
          .dispatch(getCategories(values?.project))
          .then(() => {
            thunkAPI.dispatch(getSynthesis(values?.synthesis));
            if (values?.mode === "compta" && values?.objectiveId) {
              thunkAPI.dispatch(getElementsOfObjective({
                objectiveId: values?.objectiveId,
                isPlancompta: values?.isPlancompta || false,
              }));
            }
          });
        thunkAPI.dispatch(getElementsWithSum(values?.sum));

        return data?.data;
      }

      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const deleteElementSerie = createAsyncThunk(
  "elements/deleteElementSerie",
  async (values, thunkAPI) => {
    let data;

    try {
      const response = await axios.delete(
        `/api/elements/${values.elementId}/${values.type}/${values.project.mode}`
      );
      data = await response.data;
      if (response.status === 200) {
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
        }
        if (values.isPlancompta) {
          thunkAPI.dispatch(getPlancompta(values?.project));
        }
        if (!values.isPlancompta && !values.isListing) {
          thunkAPI
            .dispatch(getCategories(values?.project))
            .then(() => thunkAPI.dispatch(getSynthesis(values?.synthesis)));
        }
        thunkAPI.dispatch(setDuplicatedElementToDelete(null));
        thunkAPI.dispatch(getElementsWithSum(values?.sum));
        if (values?.objectiveId && !values?.isListing) {
          thunkAPI.dispatch(getElementsOfObjective({
            objectiveId: values?.objectiveId,
            isPlancompta: values?.isPlancompta || false,
          }));
        }
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      console.error({ err });
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const editElementSerie = createAsyncThunk(
  "elements/editElementSerie",
  async (values, thunkAPI) => {
    let data;

    try {
      const response = await axios.put(
        `/api/elements/${values.elementId}/${values.type}/${values.project.mode}`,
        values.fieldUpdated
      );
      data = await response.data;
      if (response.status === 200) {
        // Series updates (amount, dates, décalage, category, status, …) all
        // affect footer totals — always refresh elements-sum.
        thunkAPI.dispatch(getElementsWithSum(values?.sum));
        if (values.isPlancompta) {
          thunkAPI.dispatch(getPlancompta(values?.project)).then(() => {
            thunkAPI.dispatch(getSynthesisPlancompta(values?.synthesis));
            if (
              values?.mode === "compta" &&
              values?.objectiveId &&
              !values?.isListing
            ) {
              thunkAPI.dispatch(
                getElementsOfObjective({
                  objectiveId: values?.objective?.objectiveId || values?.objectiveId,
                  isPlancompta: true,
                })
              );
            }
          });
        }
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
        } else if (!values.isPlancompta) {
          thunkAPI
            .dispatch(getCategories(values?.project))
            .then(() => {
              thunkAPI.dispatch(getSynthesis(values?.synthesis));
              if (
                values?.mode === "compta" &&
                values?.objectiveId &&
                !values?.isListing
              ) {
                thunkAPI.dispatch(
                  getElementsOfObjective({
                    objectiveId: values?.objective?.objectiveId || values?.objectiveId,
                    isPlancompta: false,
                  })
                );
              }
            });
        }
        return data?.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Une erreur est survenue";
      return thunkAPI.rejectWithValue(err.response?.data || { message: errorMessage });
    }
  }
);
const slice = createSlice({
  name: "elements",
  initialState,
  reducers: {
    setElementToEdit: (state, action) => {
      state.elementToEdit = action.payload;
    },
    setIsEditOrAdd: (state, action) => {
      state.isEditOrAdd = action.payload;
    },
    setElementToDelete: (state, action) => {
      state.elementToDelete = action.payload;
    },
    openCreatePopOver: (state, action) => {
      state.popoverCategory = action.payload;
    },
    setSimulationInputs: (state, action) => {
      state.simulationInput = action.payload.data;
      state.isCreatingInSimulationMode = action.payload.isCreating;
    },
    openReellePopup: (state, action) => {
      state.reellePopup = action.payload;
    },
    setActionType: (state, action) => {
      state.actionType = action.payload;
    },
    setElementSortByValue: (state, action) => {
      state.sortedValue = action.payload;
    },
    setUserHasSorted: (state, action) => {
      state.hasUserSorted = action.payload;
    },
    handelHoveredTotal: (state, action) => {
      state.sortedValue = action.payload;
    },
    setDuplicatedElementToDelete: (state, action) => {
      state.duplicatedElementToDelete = action.payload;
    },
    setDuplicatedElementToEdit: (state, action) => {
      state.duplicatedElementToEdit = action.payload;
    },
    setSerieToDelete: (state, action) => {
      state.serieToDelete = action.payload;
    },
    setSerieToEdit: (state, action) => {
      state.serieToEdit = action.payload;
    },
  },
  extraReducers: {
    [getElements.pending]: (state, action) => {
      state.status = "loading";
    },
    [getElements.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.elements = action.payload;
    },
    [getElements.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },
    [getElementsWithSum.pending]: (state, action) => {
      state.status = "loading";
    },
    [getElementsWithSum.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.elementsWithSum = action.payload;
    },
    [getElementsWithSum.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },
  },
});

export const reducer = slice.reducer;
export const {
  openReellePopup,
  openCreatePopOver,
  setSimulationInputs,
  setIsEditOrAdd,
  setElementToEdit,
  setElementSortByValue,
  setUserHasSorted,
  setElementToDelete,
  handelHoveredTotal,
  setActionType,
  setDuplicatedElementToDelete,
  setDuplicatedElementToEdit,
  setSerieToDelete,
  setSerieToEdit,
} = slice.actions;

export default slice;
